// Dialog „Kanal exportieren" (Kanal-Kopfzeile) mit Fortschrittsanzeige. Vertrag: docs/API.md 3.12.
import type { ExportFormat, ExportJobDto } from '@lagerfeuer/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { exportsApi } from '../api';
import type { ExtraContext } from '../index';
import { useStableHandler } from '../scheduled/hooks';
import { Button, Dialog, ErrorBox, Field } from '../ui';
import { ExportStatus } from './ExportStatus';
import { EXPORT_POLL_MS, EXPORTS_LIST_KEY, exportJobKey, isExportActive } from './realtime';
import './exports.css';

const FORMATS: { value: ExportFormat; label: string; hint: string }[] = [
  { value: 'HTML', label: 'HTML', hint: 'Eigenständige Webseite – im Browser lesbar, mit Formatierung.' },
  { value: 'MARKDOWN', label: 'Markdown', hint: 'Schlichter Text mit Markdown – gut für Notizen und Wikis.' },
  { value: 'JSON', label: 'JSON', hint: 'Maschinenlesbar mit allen Feldern – für Archive und eigene Auswertungen.' },
];

/** `YYYY-MM-DD` (lokal) → ISO des Tagesbeginns; `addDays` verschiebt (für „bis einschließlich"). */
function dayStartIso(value: string, addDays = 0): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + addDays).toISOString();
}

export function ExportChannelDialog({ channelId, onClose: onCloseProp }: ExtraContext) {
  const onClose = useStableHandler(onCloseProp);
  const qc = useQueryClient();
  const [format, setFormat] = useState<ExportFormat>('HTML');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [attachments, setAttachments] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<ExportJobDto | null>(null);

  const start = useMutation({
    mutationFn: (body: { format: ExportFormat; after?: string; before?: string; includeAttachments?: boolean }) => exportsApi.create(channelId ?? '', body),
    onSuccess: (job) => {
      qc.setQueryData(exportJobKey(job.id), job);
      void qc.invalidateQueries({ queryKey: EXPORTS_LIST_KEY });
      setCreated(job);
    },
  });

  const job = useQuery({
    queryKey: exportJobKey(created?.id ?? ''),
    queryFn: () => exportsApi.get(created?.id ?? ''),
    enabled: created !== null,
    initialData: created ?? undefined,
    refetchInterval: (q) => (q.state.data && isExportActive(q.state.data.status) ? EXPORT_POLL_MS : false),
    refetchIntervalInBackground: true,
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const after = from ? dayStartIso(from) : null;
    const before = to ? dayStartIso(to, 1) : null;
    if ((from && !after) || (to && !before)) return setError('Ungültiges Datum.');
    if (after && before && after >= before) return setError('Das Startdatum muss vor dem Enddatum liegen.');
    setError(null);
    start.mutate({ format, ...(after ? { after } : {}), ...(before ? { before } : {}), ...(attachments ? { includeAttachments: true } : {}) });
  };

  const current = job.data ?? created;
  return (
    <Dialog
      title="Kanal exportieren"
      testId="exports-dialog"
      onClose={onClose}
      footer={
        current ? (
          <Button testId="exports-dialog-close-btn" onClick={onClose}>
            Schließen
          </Button>
        ) : (
          <>
            <Button testId="exports-dialog-cancel" onClick={onClose}>
              Abbrechen
            </Button>
            <Button variant="primary" type="submit" form="lfx-export-form" testId="exports-dialog-submit" disabled={start.isPending || !channelId}>
              {start.isPending ? 'Starte …' : 'Export starten'}
            </Button>
          </>
        )
      }
    >
      {current ? (
        <div className="lfx-stack" data-testid="exports-dialog-job">
          <p className="lfx-muted lfx-exp-p">
            Der Export läuft im Hintergrund. Du kannst den Dialog schließen – fertige Exporte findest du unter{' '}
            <Link to="/exports" className="lfx-link" data-testid="exports-dialog-list-link" onClick={onClose}>
              Exporte
            </Link>
            .
          </p>
          <ExportStatus job={current} testId="exports-dialog" />
          <ErrorBox error={job.isError ? job.error : null} testId="exports-dialog-poll-error" />
        </div>
      ) : (
        <form id="lfx-export-form" className="lfx-stack" onSubmit={submit} noValidate>
          {!channelId && (
            <div className="lfx-error" role="alert" data-testid="exports-dialog-no-channel">
              Kein Kanal ausgewählt.
            </div>
          )}
          <fieldset className="lfx-exp-formats">
            <legend className="lfx-field__label">Format</legend>
            {FORMATS.map((f) => (
              <label key={f.value} className={`lfx-exp-format${format === f.value ? ' lfx-exp-format--active' : ''}`}>
                <input type="radio" name="lfx-export-format" value={f.value} checked={format === f.value} onChange={() => setFormat(f.value)} data-testid={`exports-dialog-format-${f.value.toLowerCase()}`} />
                <span>
                  <strong>{f.label}</strong>
                  <span className="lfx-muted lfx-exp-format__hint">{f.hint}</span>
                </span>
              </label>
            ))}
          </fieldset>
          <div className="lfx-exp-range">
            <Field label="Von (optional)">
              {(id) => <input id={id} type="date" className="lfx-input" data-testid="exports-dialog-from" value={from} onChange={(e) => setFrom(e.target.value)} />}
            </Field>
            <Field label="Bis einschließlich (optional)">
              {(id) => <input id={id} type="date" className="lfx-input" data-testid="exports-dialog-to" value={to} onChange={(e) => setTo(e.target.value)} />}
            </Field>
          </div>
          <label className="lfx-check">
            <input type="checkbox" data-testid="exports-dialog-attachments" checked={attachments} onChange={(e) => setAttachments(e.target.checked)} />
            Anhänge einschließen
          </label>
          {attachments && (
            <div className="lfx-muted" data-testid="exports-dialog-zip-hint">
              Mit Anhängen wird der Export als ZIP-Archiv erstellt und kann deutlich größer werden.
            </div>
          )}
          {error && (
            <div className="lfx-field__error" role="alert" data-testid="exports-dialog-validation">
              {error}
            </div>
          )}
          <ErrorBox error={start.error} testId="exports-dialog-error" />
        </form>
      )}
    </Dialog>
  );
}
